import { useState } from 'react';
import { Check, Minus, Plus } from 'lucide-react';
import type { Exercise, WorkoutSet } from '@/engine/types';
import { num } from '@/lib/format';
import { cn } from '@/lib/utils';

interface Props {
  exercise: Exercise;
  last: WorkoutSet | null;
  onAdd: (set: { reps: number; weightKg: number; rir: number; warmup: boolean }) => void;
}

/**
 * Registro de una serie, pensado para usarse CON UNA MANO entre series y con el
 * móvil en la otra (SPEC 6.D).
 *
 * Nada de teclado: todo son botones grandes. Los valores vienen precargados con
 * la última serie efectiva de ese ejercicio, que es lo que se repite el 90 % de
 * las veces.
 */
export function SetLogger({ exercise, last, onAdd }: Props) {
  const [reps, setReps] = useState(last?.reps ?? exercise.repRange.min);
  const [weight, setWeight] = useState(last?.weightKg ?? 0);
  const [rir, setRir] = useState(last?.rir ?? 2);
  const [warmup, setWarmup] = useState(false);

  return (
    <div className="flex flex-col gap-3 rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-surface-2)] p-3">
      <Stepper
        label="Repeticiones"
        value={reps}
        step={1}
        min={1}
        max={100}
        onChange={setReps}
        format={(v) => String(v)}
      />

      <Stepper
        label="Peso"
        value={weight}
        step={exercise.incrementKg}
        min={0}
        max={500}
        onChange={setWeight}
        format={(v) => `${num(v, v % 1 === 0 ? 0 : 1)} kg`}
      />

      <fieldset>
        <legend className="mb-1 text-sm font-medium">
          RIR{' '}
          <span className="font-normal text-[var(--color-text-muted)]">
            (repeticiones en reserva)
          </span>
        </legend>
        <div className="flex gap-1.5" role="radiogroup" aria-label="Repeticiones en reserva">
          {[0, 1, 2, 3, 4, 5].map((value) => (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={rir === value}
              onClick={() => setRir(value)}
              className={cn(
                'tap flex-1 rounded-xl border text-base font-semibold tabular',
                rir === value
                  ? 'border-[var(--color-brand)] bg-[var(--color-brand-soft)] text-[var(--color-brand-strong)]'
                  : 'border-[var(--color-border)] text-[var(--color-text-muted)]',
              )}
            >
              {value}
            </button>
          ))}
        </div>
      </fieldset>

      <div className="flex items-center gap-2">
        <button
          type="button"
          role="switch"
          aria-checked={warmup}
          onClick={() => setWarmup((v) => !v)}
          className={cn(
            'tap flex flex-1 items-center justify-center gap-2 rounded-xl border text-sm font-medium',
            warmup
              ? 'border-[var(--color-warn)] bg-[var(--color-warn-soft)] text-[var(--color-text)]'
              : 'border-[var(--color-border)] text-[var(--color-text-muted)]',
          )}
        >
          {warmup ? <Check aria-hidden="true" className="size-4" /> : null}
          Calentamiento
        </button>

        <button
          type="button"
          onClick={() => onAdd({ reps, weightKg: weight, rir, warmup })}
          className="tap flex-1 rounded-xl bg-[var(--color-brand-button)] px-4 font-semibold text-[var(--color-on-brand)]"
        >
          Añadir serie
        </button>
      </div>
    </div>
  );
}

function Stepper({
  label,
  value,
  step,
  min,
  max,
  onChange,
  format,
}: {
  label: string;
  value: number;
  step: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
  format: (value: number) => string;
}) {
  const clamp = (v: number) => Math.min(Math.max(Math.round(v * 100) / 100, min), max);

  return (
    <div className="flex items-center gap-2">
      <span className="w-28 shrink-0 text-sm font-medium">{label}</span>
      <button
        type="button"
        onClick={() => onChange(clamp(value - step))}
        aria-label={`Bajar ${label.toLowerCase()}`}
        className="tap w-14 rounded-xl border border-[var(--color-border)] text-[var(--color-text-muted)]"
      >
        <Minus aria-hidden="true" className="mx-auto size-5" />
      </button>
      <output className="flex-1 text-center text-lg font-semibold tabular" aria-label={label}>
        {format(value)}
      </output>
      <button
        type="button"
        onClick={() => onChange(clamp(value + step))}
        aria-label={`Subir ${label.toLowerCase()}`}
        className="tap w-14 rounded-xl border border-[var(--color-border)] text-[var(--color-text-muted)]"
      >
        <Plus aria-hidden="true" className="mx-auto size-5" />
      </button>
    </div>
  );
}
